import type { Address } from "viem"
import { useQuery } from "@tanstack/react-query"
import { useConfig } from "wagmi"

import { superVoterUserManagerOf } from "@/domains/on-chain"

import { useChainConfig } from "./use-chain-config"

/**
 * Fetches the SuperVoter manager address that `account` has delegated to.
 * `currentManagerAddress` is `Address | undefined` — consumers MUST guard
 * against undefined before passing it to `isAddressEqual`, because viem
 * throws on non-address inputs (including the raw string "undefined"). A
 * `?? zeroAddress` fallback is typically sufficient.
 */
export const useSuperVoterManagerAddress = ({ account }: { account: Address }) => {
  const config = useConfig()

  const chainConfig = useChainConfig()

  const superVoterUserManagerOfQuery = useQuery<Address | undefined>({
    queryKey: ["superVoterUserManagerOf", account],
    queryFn: () =>
      superVoterUserManagerOf({
        account,
        chainId: chainConfig.chainId,
        config,
        superVoterAddress: chainConfig.equalizer.contracts.superVoter,
      }),
    enabled: !!account,
  })

  const { data: currentManagerAddress } = superVoterUserManagerOfQuery

  return { currentManagerAddress, superVoterUserManagerOfQuery }
}
